import { create } from 'zustand';

/**
 * Coordinates the globally-mounted retro boot overlay (BootAnimation.tsx,
 * mounted once at the app root in App.tsx) across the signup → navigate →
 * Space-mount boundary — three independent places that otherwise have no way
 * to talk to each other:
 *
 *   1. useFunnelRedirect.navigateToSpaceOrIntro — activates the overlay for
 *      eligible funnels (bootAnimationConfig.ts) BEFORE calling navigate(),
 *      so it's already painted, full-screen, before the router even starts
 *      unmounting the signup page. This is what hides every generic loading
 *      screen in between (Suspense's chunk-load fallback, ProtectedRoute's
 *      auth check, SpacePageWrapper's init loaders) — they still render, but
 *      underneath the overlay.
 *   2. BootAnimation.tsx — owns the actual sequencing (loop indefinitely
 *      until ready, then greeting, then exit-fade) and flips `spaceReady`'s
 *      counterpart `revealNow` once its sequence is done.
 *   3. Space.tsx — flips `spaceReady` once it has mounted and reached the
 *      point it would otherwise reveal the desktop chrome, then watches
 *      `revealNow` to actually do so (and resets the whole store once handled).
 *   4. SpacePageWrapper.tsx — once `revealNow` has mounted the real desktop
 *      underneath the still-opaque overlay, reports `desktopReady` the
 *      moment its own "everything is loaded" signal (isUIReady +
 *      initState.status === 'initialized', plus every open window finishing
 *      its progressive mount) goes true. BootAnimation's 'mounting' stage
 *      holds the overlay fully opaque until this flips, so the desktop is
 *      never revealed while it's still hydrating.
 *
 * `active` is the single on/off switch. Only ever set once per genuine signup
 * (gated on the runtime-only `isNewUser` flag at the call site), and always
 * reset back to false by Space.tsx once consumed — so it can never leak into
 * a later, unrelated desktop mount within the same session.
 */
interface SignupBootState {
  active: boolean;
  spaceReady: boolean;
  revealNow: boolean;
  desktopReady: boolean;
  /** Begin a fresh boot sequence. Must be called before navigating away from the signup page. */
  activate: () => void;
  setSpaceReady: (spaceReady: boolean) => void;
  setRevealNow: (revealNow: boolean) => void;
  setDesktopReady: (desktopReady: boolean) => void;
  /** Tear down after the sequence has been fully handled. */
  reset: () => void;
}

export const useSignupBootStore = create<SignupBootState>((set) => ({
  active: false,
  spaceReady: false,
  revealNow: false,
  desktopReady: false,
  activate: () => set({ active: true, spaceReady: false, revealNow: false, desktopReady: false }),
  setSpaceReady: (spaceReady) => set({ spaceReady }),
  setRevealNow: (revealNow) => set({ revealNow }),
  setDesktopReady: (desktopReady) => set({ desktopReady }),
  reset: () => set({ active: false, spaceReady: false, revealNow: false, desktopReady: false }),
}));
